import { NOTIFICATION_CATEGORY_LABEL, groupNotificationsByDay } from '@/domain';
import type { Notification } from '@/application/ports/NotificationRepository';
import { formatDay, formatShortDay, formatTime, labToday } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import styles from './NotificationList.module.css';

/**
 * A person's notifications as rows: the title, the category it is in, one line
 * saying what happened to which case and who did it, and when. The request of
 * 7 October 2026, sections 1 and 2.
 *
 * Gathered by day for the centre, Today and Yesterday by name and earlier days
 * by date; in one run for the home screen, which shows a handful. A row not
 * read yet is tinted and carries a dot, and a read one recedes rather than
 * disappears. A row is one button: tapping it opens its case.
 */
export function NotificationList({
  rows,
  onOpen,
  byDay = false,
  label,
}: {
  rows: readonly Notification[];
  onOpen: (notification: Notification) => void;
  byDay?: boolean;
  /** What the list is, for a screen reader. */
  label: string;
}) {
  if (!byDay) {
    return (
      <ul className={styles.list} aria-label={label}>
        {rows.map((row) => (
          <Row key={row.id} row={row} onOpen={onOpen} withDay />
        ))}
      </ul>
    );
  }

  return (
    <div className={styles.days} aria-label={label} role="group">
      {groupNotificationsByDay(rows, labToday()).map(({ day, items }) => (
        <section key={day} className={styles.day}>
          <h3 className={styles.dayHeading}>
            {day === 'today'
              ? strings.notifications.today
              : day === 'yesterday'
                ? strings.notifications.yesterday
                : formatShortDay(day)}
          </h3>
          <ul className={styles.list}>
            {items.map((row) => (
              <Row key={row.id} row={row} onOpen={onOpen} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function Row({
  row,
  onOpen,
  withDay = false,
}: {
  row: Notification;
  onOpen: (notification: Notification) => void;
  /** The day beside the time, where no heading says it. */
  withDay?: boolean;
}) {
  // The lab's day, which is not always the day the instant is written with.
  const day = formatDay(row.createdAt);
  return (
    <li>
      <button
        type="button"
        className={`${styles.row} ${row.read ? styles.read : ''}`}
        onClick={() => onOpen(row)}
      >
        <span className={styles.top}>
          <span className={styles.title}>{row.title}</span>
          {!row.read && (
            <span className={styles.dot} role="img" aria-label={strings.notifications.unreadMark} />
          )}
          <time className={styles.when} dateTime={row.createdAt}>
            {withDay && day !== labToday() ? `${formatShortDay(day)} ` : ''}
            {formatTime(row.createdAt)}
          </time>
        </span>
        <span className={styles.text}>{row.text}</span>
        <span className={styles.category}>{NOTIFICATION_CATEGORY_LABEL[row.category]}</span>
      </button>
    </li>
  );
}
